import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { RotateCcw, Undo2 } from "lucide-react";

/** The mail page's one way of saying what an action did: a short notice at
 *  the bottom, with Undo when the action can be undone (also the z key, in
 *  every view) and Retry when it failed and can be tried again. Triage's
 *  pattern, used by every list since 2026-10-01 so an action reads the same
 *  wherever it was taken. */

export type ToastTone = "ok" | "error" | "info";
export type ToastInput = {
  text: string;
  tone: ToastTone;
  undo?: () => void;
  retry?: () => void;
  /** Another button ("Reload"). */
  action?: { label: string; run: () => void };
  ttlMs?: number;
};
type Toast = ToastInput & { id: number; ttlMs: number };

type ToastApi = {
  /** Show a notice. One with `undo` also becomes the newest thing `z` undoes. */
  toast: (t: ToastInput) => number;
  dismiss: (id: number) => void;
  /** Undo the newest undoable action; false when there is none. */
  undoLast: () => boolean;
};

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  /** Newest last. An entry leaves when it is undone, from the notice or by z. */
  const undoStack = useRef<Array<{ id: number; undo: () => void }>>([]);

  const dismiss = useCallback((id: number) => setToasts((cur) => cur.filter((x) => x.id !== id)), []);

  const toast = useCallback((t: ToastInput) => {
    const id = ++seq.current;
    const ttlMs = t.ttlMs ?? (t.tone === "error" ? 12_000 : 6_000);
    if (t.undo) {
      undoStack.current.push({ id, undo: t.undo });
      if (undoStack.current.length > 50) undoStack.current.shift();
    }
    setToasts((cur) => [...cur.slice(-3), { ...t, id, ttlMs }]);
    setTimeout(() => setToasts((cur) => cur.filter((x) => x.id !== id)), ttlMs);
    return id;
  }, []);

  const runUndo = useCallback((id: number) => {
    const i = undoStack.current.findIndex((u) => u.id === id);
    if (i < 0) return;
    const [u] = undoStack.current.splice(i, 1);
    u.undo();
    dismiss(id);
  }, [dismiss]);

  const undoLast = useCallback(() => {
    const u = undoStack.current[undoStack.current.length - 1];
    if (!u) return false;
    runUndo(u.id);
    return true;
  }, [runUndo]);

  const api = useMemo(() => ({ toast, dismiss, undoLast }), [toast, dismiss, undoLast]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 max-md:bottom-[calc(64px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={
              "wov-rise pointer-events-auto relative flex max-w-[680px] items-center gap-3 overflow-hidden rounded-xl px-3.5 py-2 wov-t-sm shadow-xl ring-1 ring-black/5 " +
              (t.tone === "error" ? "bg-rose-600 text-white" : "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900")
            }
          >
            <span className="truncate">{t.text}</span>
            {t.undo && (
              // A real button, not a link: an accidental Done or Delete is
              // put right from here (or with z) while the bar runs down.
              <button
                type="button"
                onClick={() => runUndo(t.id)}
                className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 font-bold text-slate-900 shadow-sm transition hover:bg-sky-100 active:scale-95 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-700"
              >
                <Undo2 className="h-3.5 w-3.5" /> Undo
                <kbd className="rounded bg-slate-900/10 px-1 font-mono text-[10px] font-semibold dark:bg-white/15">Z</kbd>
              </button>
            )}
            {t.undo && (
              <span aria-hidden className="wov-toast-timer absolute bottom-0 left-0 h-[3px] bg-sky-400/80" style={{ animationDuration: `${t.ttlMs}ms` }} />
            )}
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.run();
                  dismiss(t.id);
                }}
                className="inline-flex items-center gap-1 font-semibold underline"
              >
                {t.action.label}
              </button>
            )}
            {t.retry && (
              <button
                type="button"
                onClick={() => {
                  t.retry!();
                  dismiss(t.id);
                }}
                className="inline-flex items-center gap-1 font-semibold underline"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Retry
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToasts(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToasts outside ToastProvider");
  return ctx;
}
